import React, { useEffect, useState, useCallback, useMemo } from 'react';

import {
  Box, IconButton, Dialog, DialogActions, DialogContent,
  DialogTitle, Button, TablePagination, CircularProgress,
  Typography, Grid, Checkbox, FormControlLabel, Card, CardMedia, CardContent, TextField, Divider, Select, MenuItem
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import { useSearchParams } from 'react-router-dom';
import PageHeader1 from '../../components/common/PageHeader1';
import { toast } from 'react-toastify';
import { BsEye, BsDownload, BsCheck, BsX } from 'react-icons/bs';
import * as XLSX from 'xlsx';

const formatDate = (dateString) => {
  if (!dateString) return '-';
  const date = new Date(dateString);
  const day = String(date.getDate()).padStart(2, '0');
  const monthNames = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
  const month = monthNames[date.getMonth()];
  const year = date.getFullYear();
  let hours = date.getHours();
  const minutes = String(date.getMinutes()).padStart(2, '0');
  const ampm = hours >= 12 ? 'PM' : 'AM';
  hours = hours % 12;
  hours = hours ? hours : 12;
  const strHours = String(hours).padStart(2, '0');
  return `${day} ${month} ${year}, ${strHours}:${minutes} ${ampm}`;
};

const SEARCH_BY_OPTIONS = [
  { value: "orderId", label: "Order ID" },
  { value: "poojaName", label: "Pooja Name" },
  { value: "userName", label: "User Name" },
  { value: "astrologerName", label: "Astrologer Name" }
];

function PoojaImageVerification() {
  const [searchParams, setSearchParams] = useSearchParams();
  const initialPage = parseInt(searchParams.get('page') || '0', 10);
  const initialPageSize = parseInt(searchParams.get('limit') || '10', 10);

  const [paginationModel, setPaginationModel] = useState({
    page: Number.isFinite(initialPage) && initialPage >= 0 ? initialPage : 0,
    pageSize: Number.isFinite(initialPageSize) && initialPageSize > 0 ? initialPageSize : 10,
  });

  const [data, setData] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [searchText, setSearchText] = useState("");
  const [searchBy, setSearchBy] = useState(SEARCH_BY_OPTIONS[0].value);
  const [appliedSearch, setAppliedSearch] = useState("");
  const [appliedSearchBy, setAppliedSearchBy] = useState(SEARCH_BY_OPTIONS[0].value);
  const [exportLoading, setExportLoading] = useState(false);

  // View Dialog State
  const [verifyDialogOpen, setVerifyDialogOpen] = useState(false);
  const [selectedPooja, setSelectedPooja] = useState(null);
  const [proofsState, setProofsState] = useState([]);

  // Confirm Dialog State
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [confirmAction, setConfirmAction] = useState(""); // "approve" or "reject"
  const [confirmPooja, setConfirmPooja] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [adminMediaRejection, setAdminMediaRejection] = useState("");

  const fetchUnverifiedImages = useCallback(async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem("astroVani-admin-token");
      // Page from Material UI DataGrid is 0-indexed, API usually expects 1-indexed
      const pageParam = paginationModel.page + 1;
      const limitParam = paginationModel.pageSize;

      const searchParamStr = appliedSearch ? `&${appliedSearchBy}=${encodeURIComponent(appliedSearch)}` : "";

      const response = await fetch(`${process.env.REACT_APP_BASEURL}/admin/pooja/unverified?page=${pageParam}&limit=${limitParam}${searchParamStr}`, {
        headers: { Authorization: token }
      });
      const result = await response.json();

      if (response.ok) {
        const items = result?.data || [];
        const total = result?.paginationDetail?.totalDocs || items.length;
        setData(items);
        setTotalCount(total);
      } else {
        toast.error(result?.message || 'Failed to fetch unverified images');
      }
    } catch (error) {
      console.error("Error fetching unverified poojas:", error);
      toast.error('An error occurred while fetching data');
    } finally {
      setLoading(false);
    }
  }, [paginationModel.page, paginationModel.pageSize, appliedSearch, appliedSearchBy]);

  useEffect(() => {
    setSearchParams({
      page: paginationModel.page,
      limit: paginationModel.pageSize,
    });
    fetchUnverifiedImages();
  }, [paginationModel, fetchUnverifiedImages, setSearchParams]);

  const handleApplySearch = () => {
    setAppliedSearch(searchText.trim());
    setAppliedSearchBy(searchBy);
    setPaginationModel((prev) => ({ ...prev, page: 0 }));
  };

  const handleResetSearch = () => {
    setSearchText("");
    setAppliedSearch("");
    setSearchBy(SEARCH_BY_OPTIONS[0].value);
    setAppliedSearchBy(SEARCH_BY_OPTIONS[0].value);
    setPaginationModel({ page: 0, pageSize: 10 });
  };

  const handleExport = async () => {
    try {
      setExportLoading(true);
      const token = localStorage.getItem("astroVani-admin-token");
      const searchParamStr = appliedSearch ? `&${appliedSearchBy}=${encodeURIComponent(appliedSearch)}` : "";
      const response = await fetch(`${process.env.REACT_APP_BASEURL}/admin/pooja/unverified?${searchParamStr}`, {
        headers: { Authorization: token }
      });
      const result = await response.json();
      const exportData = result?.data || [];

      if (!exportData.length) {
        toast.error("No data found");
        return;
      }

      const rows = exportData.map((item) => ({
        "Order ID": item?.orderId || "-",
        "Pooja Name": item?.poojaId?.name || item?.poojaName || "-",
        "User Name (Account)": item?.userId?.fullName || "-",
        "Booking Name": item?.fullName || "-",
        "Gotra": item?.gotra || "-",
        "Nakshatra": item?.nakshatra || "-",
        "Family Members": item?.familyMemberNames || "-",
        "Purpose": item?.purposeOfPooja || "-",
        "Sankalp": item?.sankalp || "-",
        "Astrologer Name": item?.astrologerId?.fullName || item?.astrologerName || "-",
        "Images/Videos": (item?.proofUploads || item?.proofs || []).length,
      }));

      const worksheet = XLSX.utils.json_to_sheet(rows);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, "Image Verification");
      XLSX.writeFile(workbook, "Pooja_Image_Verification.xlsx");
      toast.success("Export completed successfully");
    } catch (error) {
      console.error(error);
      toast.error("Export failed");
    } finally {
      setExportLoading(false);
    }
  };

  const handleOpenVerifyDialog = (pooja) => {
    setSelectedPooja(pooja);
    const initialProofs = (pooja.proofUploads || pooja.proofs || []).map(p => ({
      url: p.url || p,
      adminApproved: p?.adminApproved !== undefined ? p?.adminApproved : false
    }));
    setProofsState(initialProofs);
    setVerifyDialogOpen(true);
  };

  const handleCloseVerifyDialog = () => {
    setVerifyDialogOpen(false);
    setSelectedPooja(null);
    setProofsState([]);
  };

  const handleOpenConfirm = (pooja, action) => {
    setConfirmPooja(pooja);
    setConfirmAction(action);
    setConfirmOpen(true);
  };

  const handleCloseConfirm = () => {
    setConfirmOpen(false);
    setConfirmPooja(null);
    setConfirmAction("");
    setAdminMediaRejection("");
  };

  const handleConfirmSubmit = async () => {
    if (!confirmPooja) return;
    if (confirmAction === 'reject' && !adminMediaRejection.trim()) {
      toast.error("Rejection reason is required");
      return;
    }
    setSubmitting(true);
    try {
      const token = localStorage.getItem("astroVani-admin-token");
      const isApprove = confirmAction === "approve";

      const payload = {
        verifiedByAdmin: isApprove
      };

      if (!isApprove) {
        payload.adminMediaRejection = adminMediaRejection;
      }

      const response = await fetch(`${process.env.REACT_APP_BASEURL}/admin/pooja/verify/${confirmPooja._id || confirmPooja.id}`, {
        method: 'PATCH',
        headers: {
          'Authorization': token,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload)
      });
      const result = await response.json();

      if (response.ok) {
        toast.success(`Images ${isApprove ? 'approved' : 'rejected'} successfully!`);
        handleCloseConfirm();
        handleCloseVerifyDialog();
        fetchUnverifiedImages();
      } else {
        toast.error(result?.message || `Failed to ${confirmAction} images`);
      }
    } catch (error) {
      console.error(`Error during ${confirmAction}:`, error);
      toast.error('An error occurred during verification');
    } finally {
      setSubmitting(false);
    }
  };

  const columns = [
    {
      field: 'sno',
      headerName: 'S.No.',
      width: 70,
      sortable: false,
      renderCell: (params) => {
        const index = params.api.getAllRowIds().indexOf(params.id);
        return (paginationModel.page * paginationModel.pageSize) + index + 1;
      }
    },
    {
      field: 'actions',
      headerName: 'Action',
      width: 150,
      sortable: false,
      renderCell: (params) => {
        return (
          <>
            <IconButton color="primary" size="small" onClick={() => handleOpenVerifyDialog(params.row)} title="View Images">
              <BsEye />
            </IconButton>
            <IconButton color="success" size="small" onClick={() => handleOpenConfirm(params.row, 'approve')} title="Approve">
              <BsCheck />
            </IconButton>
            <IconButton color="error" size="small" onClick={() => handleOpenConfirm(params.row, 'reject')} title="Reject">
              <BsX />
            </IconButton>
          </>
        );
      }
    },
    {
      field: 'orderId',
      headerName: 'Order ID',
      width: 180,
      valueGetter: (value, row) => row?.orderId || '-'
    },
    {
      field: 'poojaName',
      headerName: 'Pooja Name',
      flex: 1,
      minWidth: 150,
      valueGetter: (value, row) => row?.poojaId?.name || row?.poojaName || '-'
    },
    {
      field: 'userName',
      headerName: 'User Name',
      flex: 1,
      minWidth: 150,
      valueGetter: (value, row) => row?.userId?.fullName || '-'
    },
    {
      field: 'astrologerName',
      headerName: 'Astrologer Name',
      flex: 1,
      minWidth: 150,
      valueGetter: (value, row) => row?.astrologerId?.fullName || row?.astrologerName || '-'
    },
    {
      field: 'proofsCount',
      headerName: 'Images/Videos',
      width: 130,
      valueGetter: (value, row) => (row?.proofUploads || row?.proofs || []).length
    },
    {
      field: 'createdAt',
      headerName: 'Created At',
      width: 170,
      valueGetter: (value, row) => formatDate(row?.createdAt)
    },
    {
      field: 'updatedAt',
      headerName: 'Updated At',
      width: 170,
      valueGetter: (value, row) => formatDate(row?.updatedAt)
    },
  ];

  return (
    <div className="container-xxl">
      <PageHeader1
        headerTitle="Creative Verification"
        isExport={false}
      />

      <Box sx={{ width: '100%', bgcolor: 'background.paper', p: 2, borderRadius: 2, boxShadow: 1 }}>
        <Box display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={2} mb={2}>
          <Typography variant="h6" fontWeight={600}>
            Creative Verification
          </Typography>
          <Box display="flex" gap={1} flexWrap="wrap">
            <Select
              size="small"
              value={searchBy}
              onChange={(e) => setSearchBy(e.target.value)}
              sx={{ minWidth: 140, bgcolor: 'white' }}
            >
              {SEARCH_BY_OPTIONS.map((opt) => (
                <MenuItem key={opt.value} value={opt.value}>
                  {opt.label}
                </MenuItem>
              ))}
            </Select>
            <TextField
              size="small"
              placeholder={`Search by ${SEARCH_BY_OPTIONS.find(o => o.value === searchBy)?.label}...`}
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  handleApplySearch();
                }
              }}
            />
            <Button variant="contained" onClick={handleApplySearch}>
              Search
            </Button>
            <Button variant="outlined" onClick={handleResetSearch}>
              Reset
            </Button>
            <Button
              variant="contained"
              color="success"
              startIcon={exportLoading ? <CircularProgress size={16} color="inherit" /> : <BsDownload />}
              onClick={handleExport}
              disabled={exportLoading}
            >
              {exportLoading ? "Exporting..." : "Export"}
            </Button>
          </Box>
        </Box>
        <DataGrid
          rows={data}
          columns={columns}
          getRowId={(row) => row._id || row.id}
          rowCount={totalCount}
          loading={loading}
          paginationMode="server"
          paginationModel={paginationModel}
          onPaginationModelChange={setPaginationModel}
          pageSizeOptions={[10, 20, 50]}
          disableRowSelectionOnClick
          autoHeight
        />
      </Box>

      <Dialog open={verifyDialogOpen} onClose={handleCloseVerifyDialog} maxWidth="md" fullWidth>
        <DialogTitle>Verify Pooja Images</DialogTitle>
        <DialogContent dividers>
          {selectedPooja && (
            <Box mb={3}>
              <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                Booking Details
              </Typography>
              <Grid container spacing={2}>
                {selectedPooja.fullName && (
                  <Grid item xs={12} sm={6} md={4}>
                    <Typography variant="caption" color="textSecondary" display="block">Name</Typography>
                    <Typography variant="body2">{selectedPooja.fullName}</Typography>
                  </Grid>
                )}
                {selectedPooja.gotra && (
                  <Grid item xs={12} sm={6} md={4}>
                    <Typography variant="caption" color="textSecondary" display="block">Gotra</Typography>
                    <Typography variant="body2">{selectedPooja.gotra}</Typography>
                  </Grid>
                )}
                {selectedPooja.nakshatra && (
                  <Grid item xs={12} sm={6} md={4}>
                    <Typography variant="caption" color="textSecondary" display="block">Nakshatra</Typography>
                    <Typography variant="body2">{selectedPooja.nakshatra}</Typography>
                  </Grid>
                )}
                {selectedPooja.familyMemberNames && (
                  <Grid item xs={12} sm={6} md={4}>
                    <Typography variant="caption" color="textSecondary" display="block">Family Members</Typography>
                    <Typography variant="body2">{selectedPooja.familyMemberNames}</Typography>
                  </Grid>
                )}
                {selectedPooja.purposeOfPooja && (
                  <Grid item xs={12} sm={6} md={4}>
                    <Typography variant="caption" color="textSecondary" display="block">Purpose</Typography>
                    <Typography variant="body2">{selectedPooja.purposeOfPooja}</Typography>
                  </Grid>
                )}
                {selectedPooja.sankalp && (
                  <Grid item xs={12} sm={6} md={4}>
                    <Typography variant="caption" color="textSecondary" display="block">Sankalp</Typography>
                    <Typography variant="body2">{selectedPooja.sankalp}</Typography>
                  </Grid>
                )}
                {selectedPooja.completionNotes && (
                  <Grid item xs={12}>
                    <Typography variant="caption" color="textSecondary" display="block">Completion Notes</Typography>
                    <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>{selectedPooja.completionNotes}</Typography>
                  </Grid>
                )}
              </Grid>
              <Divider sx={{ mt: 2 }} />
            </Box>
          )}

          {proofsState.length === 0 ? (
            <Typography>No proofs available for this pooja.</Typography>
          ) : (
            <Grid container spacing={2}>
              {proofsState.map((proof, index) => {
                const isVideo = proof.url.toLowerCase().endsWith('.mp4') || proof.url.toLowerCase().includes('video');
                return (
                  <Grid item xs={12} sm={6} md={4} key={index}>
                    <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                      {isVideo ? (
                        <CardMedia
                          component="video"
                          controls
                          src={proof.url}
                          sx={{ height: 200, objectFit: 'contain', bgcolor: 'black' }}
                        />
                      ) : (
                        <CardMedia
                          component="img"
                          image={proof.url}
                          alt={`Proof ${index + 1}`}
                          sx={{ height: 200, objectFit: 'contain', bgcolor: '#f5f5f5' }}
                        />
                      )}
                    </Card>
                  </Grid>
                );
              })}
            </Grid>
          )}
        </DialogContent>
        <DialogActions>
          <Box sx={{ display: "flex", justifyContent: "space-between", width: "100%" }}>
            <Button onClick={handleCloseVerifyDialog} variant='outlined' color="secondary">
              Close
            </Button>
            <Box sx={{ display: "flex", gap: 1 }}>
              <Button
                onClick={() => handleOpenConfirm(selectedPooja, 'reject')}
                variant="contained"
                color="error"
                startIcon={<BsX />}
              >
                Reject
              </Button>
              <Button
                onClick={() => handleOpenConfirm(selectedPooja, 'approve')}
                variant="contained"
                color="success"
                startIcon={<BsCheck />}
              >
                Approve
              </Button>
            </Box>
          </Box>
        </DialogActions>
      </Dialog>

      <Dialog open={confirmOpen} onClose={handleCloseConfirm} maxWidth="xs" fullWidth>
        <DialogTitle>{confirmAction === 'approve' ? 'Approve' : 'Reject'} Verification</DialogTitle>
        <DialogContent dividers>
          <Typography mb={confirmAction === 'reject' ? 2 : 0}>
            Are you sure you want to {confirmAction} the verification for this Pooja?
          </Typography>
          {confirmAction === 'reject' && (
            <TextField
              label="Rejection Reason"
              fullWidth
              multiline
              rows={3}
              value={adminMediaRejection}
              onChange={(e) => setAdminMediaRejection(e.target.value)}
              placeholder="Enter rejection reason..."
            />
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseConfirm} color="inherit" disabled={submitting}>
            Cancel
          </Button>
          <Button onClick={handleConfirmSubmit} variant="contained" color={confirmAction === 'approve' ? 'success' : 'error'} disabled={submitting}>
            {submitting ? <CircularProgress size={24} color="inherit" /> : `Yes, ${confirmAction}`}
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
}

export default PoojaImageVerification;
